// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Globe, TrendingDown, Shield, ArrowRight,
  Building2, Award, Accessibility
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';
import ClientLogos from '../components/ClientLogos';

// Trust bar. Each tooltip says where the claim actually comes from; the visible labels are unchanged.
const TRUST_BADGES = [
  {
    icon: Shield,
    label: '99.9% Uptime SLA',
    tip: 'Availability target for the hosted service. Infrastructure is provided by Render, running on AWS, GCP and Cloudflare.',
  },
  {
    icon: Award,
    label: 'Built on SOC 2',
    tip: 'SOC 2 Type II audit held by Render, our hosting platform. It covers the infrastructure HousingAI runs on.',
  },
  {
    icon: Award,
    label: 'Built on ISO 27001',
    tip: 'ISO/IEC 27001:2022 certification held by Render, our hosting platform. It covers the infrastructure HousingAI runs on.',
  },
  {
    icon: Accessibility,
    label: 'WCAG 2.2 AA',
    tip: 'Accessibility target for the resident-facing widget, based on our own review rather than a third-party audit.',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [hoveredBox, setHoveredBox] = useState(null);
  const [showExText, setShowExText] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [exTextTimeout, setExTextTimeout] = useState(null);
  const [currentQAIndex, setCurrentQAIndex] = useState(0);
  const [typingState, setTypingState] = useState('idle'); // idle, typing-question, loading, typing-response, complete
  const [typedQuestion, setTypedQuestion] = useState('');
  const [typedResponse, setTypedResponse] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [tappedBox, setTappedBox] = useState(null);

  // Detect if we're on a mobile/narrow screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Rotating messages
  const rotatingMessages = [
    "Need a chat feature to assist families with navigating the programs & services your Housing Authority provides?",
    "Add a chatbot to your website.",
    "Empower your housing authority with AI-powered resident support that breaks down language barriers, reduces call volumes, and ensures fair housing compliance.",
    "Revolutionizing access to subsidized housing through Artificial Intelligence and Expert Knowledge."
  ];

  // Q&A sets data
  const qaSets = [
    {
      header: "Program & Waiting List Questions",
      question: "How long does it take to get Section 8 housing?",
      response: "Because many people apply for housing assistance, Section 8 waiting lists often become quite long, and it may take several years before your application reaches the top of the list.\nWould you like guidance on how to apply or do you want to see other housing options?",
      advantage: "Assists applicants with frequently asked questions immediately, reducing unnecessary phone calls."
    },
    {
      header: "Maintenance Requests",
      question: "How can I request maintenance?",
      response: "You can submit maintenance requests via our online portal. Would you like me to guide you through that process right now?",
      advantage: "Assists residents with submitting requests immediately, reducing unnecessary phone calls."
    },
    {
      header: "Rent Payment",
      question: "How do I pay my rent?",
      response: "Three (3) methods available for tenants to pay rent to the Housing Authority:\na) Direct withdrawal from tenant's account\nb) Mail check/money order with payment coupon\nc) Pay cash in person at the bank\nShall I walk you through any of these methods?",
      advantage: "Streamlines payments, ensures timely rent collection, and reduces administrative load."
    },
    {
      header: "Eligibility Questions",
      question: "Am I eligible for housing assistance?",
      response: "Eligibility depends on income, family size, and other criteria. I'll ask a few quick questions to determine your eligibility.\nShall we start?",
      advantage: "Quickly screens applicant eligibility, saving time for both applicants and staff."
    },
    {
      header: "Language Assistance",
      question: "(Ask in Spanish) ¿Cómo solicito asistencia para la vivienda?",
      response: "(Responds in Spanish) Puedo ayudarle con eso. Las solicitudes se realizan en línea.\n¿Quiere que le guíe por el proceso?",
      advantage: "Instant multilingual responses ensure equal access and improve resident satisfaction."
    }
  ];

  // Cycle through rotating messages
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % rotatingMessages.length);
    }, 5000); // Allow 5 seconds to read each
    return () => clearInterval(interval);
  }, []);

  // Q&A animation sequence
  useEffect(() => {
    const currentQA = qaSets[currentQAIndex];

    const runSequence = async () => {
      // Reset states
      setTypingState('idle');
      setTypedQuestion('');
      setTypedResponse('');

      // Wait for header and advantage to appear
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Type question
      setTypingState('typing-question');
      for (let i = 0; i <= currentQA.question.length; i++) {
        setTypedQuestion(currentQA.question.slice(0, i));
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      // Shake effect and loading
      setTypingState('loading');
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Type response
      setTypingState('typing-response');
      for (let i = 0; i <= currentQA.response.length; i++) {
        setTypedResponse(currentQA.response.slice(0, i));
        await new Promise(resolve => setTimeout(resolve, 20));
      }

      setTypingState('complete');
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Move to next Q&A
      setCurrentQAIndex((prev) => (prev + 1) % qaSets.length);
    };

    runSequence();
  }, [currentQAIndex]);

  const metrics = [
    {
      icon: Globe,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      value: '10+',
      label: 'Languages Supported',
      exText: 'Break down language barriers. Ensure equal access to housing information for all residents.'
    },
    {
      icon: Accessibility,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      value: '100%',
      label: 'ADA Compliance',
      exText: 'Full accessibility: screen reader, keyboard, and voice control.'
    },
    {
      icon: TrendingDown,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      value: '60%',
      label: 'Call Volume Reduction',
      exText: 'Let residents get instant answers to common questions while your staff handles complex cases.'
    },
    {
      icon: Shield,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      value: '24/7/365',
      label: 'Service Availability',
      exText: 'Build resident trust with a dependable platform that works around the clock.'
    }
  ];

  const getBoxWidth = (index) => {
    if (isMobile) return '100%'; // Full width on mobile
    if (hoveredBox === null) return '25%';
    if (hoveredBox === index) return '40%';
    // Distribute remaining 60% among other 3 boxes
    return '20%';
  };

  const getBoxHeight = (index) => {
    if (!isMobile) return '16rem'; // h-64 = 16rem
    // On mobile, expand vertically
    if (tappedBox === index) return '20rem'; // Taller when expanded
    return '16rem';
  };

  const handleMouseEnter = (index) => {
    if (isMobile) return; // Don't handle hover on mobile
    
    // Clear any existing timeouts
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (exTextTimeout) {
      clearTimeout(exTextTimeout);
      setExTextTimeout(null);
    }

    setHoveredBox(index);

    // Delay showing ex_text until box expansion is complete
    const textTimeout = setTimeout(() => {
      setShowExText(index);
      setExTextTimeout(null);
    }, 300); // Match the box expansion duration
    setExTextTimeout(textTimeout);
  };

  const handleMouseLeave = () => {
    if (isMobile) return; // Don't handle hover on mobile
    
    // Immediately hide ex_text
    setShowExText(null);

    // Clear ex_text timeout if it exists
    if (exTextTimeout) {
      clearTimeout(exTextTimeout);
      setExTextTimeout(null);
    }

    // Add a small delay before removing hover state to prevent jitter
    const timeout = setTimeout(() => {
      setHoveredBox(null);
      setHoverTimeout(null);
    }, 100);
    setHoverTimeout(timeout);
  };

  const handleTap = (index) => {
    if (!isMobile) return; // Only handle tap on mobile
    
    if (tappedBox === index) {
      // If tapping the same box, close it
      setTappedBox(null);
      setShowExText(null);
    } else {
      // Close any open box and open the new one
      setTappedBox(index);
      setShowExText(null);
      
      // Show text after animation
      setTimeout(() => {
        setShowExText(index);
      }, 300);
    }
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      if (exTextTimeout) {
        clearTimeout(exTextTimeout);
      }
    };
  }, [hoverTimeout, exTextTimeout]);

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-12 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              24/7 Multilingual Resident Support
              <span className="text-blue-600"> for a Fraction of the Cost</span>
            </motion.h1>

            <div className="h-20 relative mb-8">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentMessageIndex}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-xl text-gray-600 max-w-3xl mx-auto absolute inset-0 flex items-center justify-center"
                >
                  {rotatingMessages[currentMessageIndex]}
                </motion.p>
              </AnimatePresence>
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            >
              <button
                onClick={() => navigate('/demo')}
                className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2"
              >
                Live Demo <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-all"
              >
                See Pricing
              </button>
            </motion.div>

            {/* Metrics with expansion */}
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className={`${isMobile ? 'grid grid-cols-2 gap-4' : 'flex gap-4'} mb-16`}
            >
              {metrics.map((metric, index) => {
                const Icon = metric.icon;
                const isSquished = !isMobile && hoveredBox !== null && hoveredBox !== index;
                const isExpanded = isMobile ? tappedBox === index : hoveredBox === index;

                return (
                  <motion.div
                    key={index}
                    style={{ 
                      width: isMobile ? '100%' : getBoxWidth(index),
                      height: getBoxHeight(index)
                    }}
                    animate={{ 
                      width: isMobile ? '100%' : getBoxWidth(index),
                      height: getBoxHeight(index)
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    className={`bg-white rounded-2xl p-4 md:p-6 shadow-xl cursor-pointer relative ${isMobile ? '' : 'overflow-hidden'}`}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleTap(index)}
                  >
                    <div className="flex flex-col items-center justify-center h-full relative">
                      <motion.div
                        className="text-center"
                        animate={{
                          scale: isSquished ? 0.85 : 1,
                          y: (isExpanded && showExText === index) ? (isMobile ? -30 : -50) : 0
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon className={`h-8 md:h-12 w-8 md:w-12 ${metric.color} mx-auto mb-2 md:mb-3`} />
                        <div className="text-2xl md:text-3xl font-bold text-gray-900">{metric.value}</div>
                        <div className="text-sm md:text-base text-gray-600">{metric.label}</div>
                      </motion.div>

                      <AnimatePresence>
                        {showExText === index && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className={`${metric.bgColor} rounded-lg p-2 md:p-3 absolute ${
                              isMobile 
                                ? 'bottom-4 left-4 right-4' 
                                : 'bottom-1 left-1 right-1'
                            }`}
                          >
                            <p className="text-xs md:text-sm font-medium text-gray-800 text-center leading-tight">
                              {metric.exText}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Demo Chat Window */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl mx-auto mb-16"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <motion.span
                  key={currentQAIndex}
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-sm font-semibold text-gray-700 text-right"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                >
                  {qaSets[currentQAIndex].header}
                </motion.span>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 relative overflow-hidden">
                <div className="space-y-4 h-52 relative flex flex-col justify-center">
                  {typingState !== 'idle' && (
                    <motion.div
                      className="flex justify-end"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <motion.div
                        className="bg-blue-600 text-white rounded-2xl px-4 py-2 max-w-xs text-left"
                        animate={typingState === 'loading' ? { x: [-2, 2, -2, 2, 0] } : {}}
                        transition={{ duration: 0.2, repeat: typingState === 'loading' ? 2 : 0 }}
                      >
                        {typedQuestion}
                      </motion.div>
                    </motion.div>
                  )}

                  {(typingState === 'loading' || typingState === 'typing-response' || typingState === 'complete') && (
                    <div className="flex justify-start">
                      {typingState === 'loading' ? (
                        <div className="bg-gray-200 rounded-2xl px-4 py-3">
                          <motion.div className="flex gap-1">
                            {[0, 1, 2].map((i) => (
                              <motion.div
                                key={i}
                                className="w-2 h-2 bg-gray-500 rounded-full"
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
                              />
                            ))}
                          </motion.div>
                        </div>
                      ) : (
                        <div className="bg-gray-200 text-gray-800 rounded-2xl px-4 py-2 max-w-sm text-left whitespace-pre-wrap">
                          {typedResponse}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <motion.div
                key={`advantage-${currentQAIndex}`}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mt-4 text-left"
              >
                <p className="text-sm font-semibold text-gray-700" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {qaSets[currentQAIndex].advantage}
                </p>
              </motion.div>
            </motion.div>

            {/* Trust Signals */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-16 pt-8 border-t border-gray-200"
            >
              <h2 className="text-sm font-normal text-gray-600 mb-6 text-center">Trusted by Housing Authorities</h2>
              <ClientLogos size="compact" showNames={false} />

              <div className="mt-10 pt-8 border-t border-gray-100 flex flex-wrap justify-center items-center gap-8">
                {TRUST_BADGES.map(({ icon: Icon, label, tip }) => (
                  <div key={label} tabIndex={0} className="relative group outline-none">
                    <div className="flex items-center gap-2 grayscale opacity-60 transition duration-200 group-hover:grayscale-0 group-hover:opacity-100 group-focus:grayscale-0 group-focus:opacity-100">
                      <Icon className="h-8 w-8" />
                      <span className="font-semibold">{label}</span>
                    </div>
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-3 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white text-left opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100 group-focus:opacity-100"
                    >
                      {tip}
                      <span className="absolute left-1/2 top-full -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900" />
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default HomePage;

// ===== END OF src/pages/HomePage.js =====